import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import type { Employee as EmployeeType } from '@/types/app';

import Birthdate from './fields/Birthdate';
import DeleteButton from './fields/DeleteButton';
import Gender from './fields/Gender';
import Name from './fields/Name';
import Select from './fields/Select';
import Wage from './fields/Wage';
import SwipeToDelete from './SwipeToDelete';

/** Adds swipe-to-delete around the mobile row, but only when the row is editable. */
const withMobileDelete = (
	row: React.JSX.Element,
	{ readOnly, onDelete }: { readOnly: boolean; onDelete: () => void }
): React.JSX.Element => {
	if (readOnly) return row;
	return <SwipeToDelete onDelete={onDelete}>{row}</SwipeToDelete>;
};

interface EmployeeProps {
	employee: EmployeeType;
	idx: number;
	nameRefs: React.MutableRefObject<Record<number, HTMLInputElement | null>>;
	showValidation?: boolean;
	onContractTypeChange?: (idx: number, oldValue: string, newValue: string) => void;
	/** Read-only presentation: fields are disabled and the delete control is hidden. */
	readOnly?: boolean;
}

const Employee = ({
	employee,
	idx,
	nameRefs,
	showValidation = false,
	onContractTypeChange,
	readOnly = false
}: EmployeeProps): React.JSX.Element => {
	const { t } = useTranslation();
	const { premiumVarManagement } = useAppContext();

	const handleContractTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const oldValue = employee.contract_type || '';
		const newValue = e.target.value;
		premiumVarManagement.employees.update(idx, 'contract_type', newValue);
		onContractTypeChange?.(idx, oldValue, newValue);
	};
	return (
		<div data-employee-idx={idx} className="mb-3 sm:mb-2 transition-all duration-300">
			<div className="p-0 select-none">
				{/* Desktop layout: grid */}
				<div className="hidden sm:grid sm:grid-cols-[60px_2fr_1.5fr_1.5fr_2fr] gap-2 w-full">
					<div>
						<Gender
							employee={employee}
							idx={idx}
							updateEmployee={premiumVarManagement.employees.update}
							showValidation={showValidation}
							disabled={readOnly}
						/>
					</div>
					<div>
						<Name
							employee={employee}
							idx={idx}
							onChange={premiumVarManagement.employees.update}
							nameRefs={nameRefs}
							disabled={readOnly}
						/>
					</div>
					<div>
						<Birthdate
							value={employee.birth_date}
							onChange={(value) => premiumVarManagement.employees.update(idx, 'birth_date', value)}
							idx={idx}
							showValidation={showValidation}
							disabled={readOnly}
						/>
					</div>
					<div>
						<Wage
							employee={employee}
							idx={idx}
							onChange={premiumVarManagement.employees.update}
							showValidation={showValidation}
							disabled={readOnly}
						/>
					</div>
					<div className="flex items-center">
						<div className="flex-1 min-w-0">
							<Select
								value={employee.contract_type}
								options={[
									{
										value: '',
										display: t('quotes.settings.employees.contractTypeOptions.unknown')
									},
									{
										value: 'onb',
										display: t('quotes.settings.employees.contractTypeOptions.permanent')
									},
									{
										value: 'bep',
										display: t('quotes.settings.employees.contractTypeOptions.temporary')
									}
								]}
								onChange={handleContractTypeChange}
								idx={idx}
								showValidation={showValidation}
								disabled={readOnly}
							/>
						</div>
						{!readOnly && (
							<DeleteButton onClick={() => premiumVarManagement.employees.remove(idx)} />
						)}
					</div>
				</div>

				{/* Mobile layout: stacked with combined fields, swipe to delete */}
				<div className="sm:hidden">
					{withMobileDelete(
						<div className="space-y-2 border border-border rounded-md p-4 bg-muted/50 md:bg-background">
							{/* Row 1: Gender + Name */}
							<div className="flex gap-2">
								<div className="shrink-0">
									<Gender
										employee={employee}
										idx={idx}
										updateEmployee={premiumVarManagement.employees.update}
										showValidation={showValidation}
										disabled={readOnly}
									/>
								</div>
								<div className="flex-1 min-w-0">
									<Name
										employee={employee}
										idx={idx}
										onChange={premiumVarManagement.employees.update}
										nameRefs={nameRefs}
										disabled={readOnly}
									/>
								</div>
							</div>

							{/* Row 2: Birthdate + Wage */}
							<div className="grid grid-cols-2 gap-2">
								<div>
									<label className="text-xs text-muted-foreground mb-1 block">
										{t('quotes.settings.employees.birthdate')}
									</label>
									<Birthdate
										value={employee.birth_date}
										onChange={(value) =>
											premiumVarManagement.employees.update(idx, 'birth_date', value)
										}
										idx={idx}
										showValidation={showValidation}
										disabled={readOnly}
									/>
								</div>
								<div>
									<label className="text-xs text-muted-foreground mb-1 block">
										{t('quotes.settings.employees.wage')}
									</label>
									<Wage
										employee={employee}
										idx={idx}
										onChange={premiumVarManagement.employees.update}
										showValidation={showValidation}
										disabled={readOnly}
									/>
								</div>
							</div>

							{/* Row 3: Contract Type only (delete via swipe) */}
							<div>
								<label className="text-xs text-muted-foreground mb-1 block">
									{t('quotes.settings.employees.contractType')}
								</label>
								<Select
									value={employee.contract_type}
									options={[
										{
											value: '',
											display: t('quotes.settings.employees.contractTypeOptions.unknown')
										},
										{
											value: 'onb',
											display: t('quotes.settings.employees.contractTypeOptions.permanent')
										},
										{
											value: 'bep',
											display: t('quotes.settings.employees.contractTypeOptions.temporary')
										}
									]}
									onChange={handleContractTypeChange}
									idx={idx}
									showValidation={showValidation}
									disabled={readOnly}
								/>
							</div>
						</div>,
						{ readOnly, onDelete: () => premiumVarManagement.employees.remove(idx) }
					)}
				</div>
			</div>
		</div>
	);
};
export default Employee;
